import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../../core/services/firebase/config';
import { Building2, Globe, CheckCircle, Clock } from 'lucide-react';

interface TenantData {
  id: string;
  businessName: string;
  slug: string;
  ownerId: string;
  status?: string;
  verified?: boolean;
  plan?: string;
  createdAt: any;
}

export const TenantsListPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Real-time subscription to tenants collection
    const q = query(collection(db, 'tenants'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tenantData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TenantData[];
      
      setTenants(tenantData);
      setLoading(false);
    }, (error) => {
      // If index is missing or other error, fallback to no sort
      console.warn("Error fetching tenants (trying without sort):", error);
      const qFallback = collection(db, 'tenants');
      onSnapshot(qFallback, (snapshot) => {
          const tenantData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as TenantData[];
          setTenants(tenantData);
          setLoading(false);
      });
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    // Handle Firestore Timestamp or Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-6">
       <div className="flex justify-between items-center">
         <div>
           <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Empresas e Negócios</h1>
           <p className="text-slate-500 dark:text-slate-400">
             Total de {tenants.length} empresas cadastradas
           </p>
         </div>
       </div>

       <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          {loading ? (
             <div className="p-12 text-center text-slate-500">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                Carregando empresas...
             </div>
          ) : tenants.length === 0 ? (
             <div className="p-12 text-center text-slate-500">
                Nenhuma empresa encontrada.
             </div>
          ) : (
             <div className="overflow-x-auto">
                <table className="w-full text-left bg-white dark:bg-slate-900">
                   <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                     <tr>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Empresa</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Domínio/Slug</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Criado em</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Ações</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {tenants.map(tenant => (
                        <tr key={tenant.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                           <td className="p-4">
                              <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 flex items-center justify-center">
                                    <Building2 size={20} />
                                 </div>
                                 <div>
                                    <p className="font-bold text-slate-900 dark:text-white">{tenant.businessName || 'Sem nome'}</p>
                                    <p className="text-xs text-slate-400 font-mono text-[10px] uppercase">ID: {tenant.id.substring(0,8)}</p>
                                 </div>
                              </div>
                           </td>
                           <td className="p-4">
                              <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                                 <Globe size={14} />
                                 <a href={`https://${tenant.slug}.nossapesca.com`} target="_blank" rel="noreferrer" className="hover:underline">
                                    {tenant.slug}
                                 </a>
                              </div>
                           </td>
                           <td className="p-4">
                              {tenant.verified ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                   <CheckCircle size={12} /> Verificado
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                   <Clock size={12} /> Pendente
                                </span>
                              )}
                           </td>
                           <td className="p-4 text-sm text-slate-500 font-mono">
                              {formatDate(tenant.createdAt)}
                           </td>
                           <td className="p-4 text-right">
                              <button className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors">
                                 Gerenciar
                              </button>
                           </td>
                        </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          )}
       </div>
    </div>
  );
};
